<script lang="ts">
  import type { CurrentPlan } from "$lib/billing/offers";
  import type { PlanOffer, PortalAccessLevel } from "$lib/api/contract";
  import { offerViews, type OfferView, type UpgradeTargetPlan } from "$lib/billing/offers";
  import { createUpgradeSession } from "$lib/api/endpoints";
  import { rememberUpgradeIntent } from "$lib/billing/upgrade-fulfillment";
  import { newIdempotencyKey } from "$lib/api/idempotency";
  import { AppError } from "$lib/api/problem";
  import { toasts } from "$lib/stores/toasts.svelte";
  import { track } from "$lib/telemetry/emit";
  import PlanCard from "$lib/components/PlanCard.svelte";
  import Button from "$lib/components/Button.svelte";

  /**
   * The in-app upgrade offer: what this organization can move up to, priced from the
   * plans endpoint and never from a literal in this file.
   *
   * It owns the offer and the redirect out to Stripe, and nothing after it — the
   * `?upgrade=…` return query is AppShell's, so a banner that unmounts on the way
   * back cannot drop a fulfillment.
   */
  interface Props {
    orgId: string;
    currentPlan: CurrentPlan;
    currentAccessLevel: PortalAccessLevel;
    offers: PlanOffer[];
    /** Seats currently held, so an oversized Pro account is not shown a Team it cannot buy. */
    licensedSeats?: number | null;
    /** Called after the 7-day snooze is recorded, so the shell can hide the banner now. */
    onSnooze: () => void;
  }
  const { orgId, currentPlan, currentAccessLevel, offers, licensedSeats = null, onSnooze }: Props = $props();

  const SNOOZE_KEY = "epidev.upgradeBanner.snoozedUntil";
  const SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;

  const uid = $props.id();
  const titleId = `${uid}-upgrade-title`;

  let redirectingPlan = $state<UpgradeTargetPlan | null>(null);
  // One key per target plan, minted on first attempt and rotated after a failure.
  const attemptKeys: Record<string, string> = {};

  // Eligibility and pricing are shared with the Billing plan strip and the Overview
  // plan card, so all three agree on what is offerable and what it costs.
  const views = $derived<OfferView[]>(offerViews(offers, currentAccessLevel, currentPlan, { licensedSeats }));

  function keyFor(plan: UpgradeTargetPlan): string {
    return (attemptKeys[plan] ??= newIdempotencyKey());
  }

  async function startUpgrade(plan: UpgradeTargetPlan): Promise<void> {
    if (redirectingPlan !== null) return;
    redirectingPlan = plan;
    try {
      const upgradeSession = await createUpgradeSession(
        orgId,
        { targetPlan: plan, billingInterval: "month" },
        keyFor(plan)
      );
      track({ name: "billing.upgrade_started", targetPlan: plan, billingInterval: "month", source: "banner" });
      rememberUpgradeIntent(upgradeSession, plan);
      // Left busy on purpose: the navigation is the end of this component's life.
      window.location.assign(upgradeSession.url);
    } catch (error) {
      // A refused attempt is a new attempt — replaying its key would replay the refusal.
      attemptKeys[plan] = newIdempotencyKey();
      redirectingPlan = null;
      if (error instanceof AppError) toasts.error(error.userMessage, error.correlationId);
    }
  }

  function snooze(): void {
    try {
      window.localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_MS));
    } catch {
      // Storage denied (private mode, blocked cookies): the dismissal still has to
      // land for this session, it just will not survive a reload.
    }
    onSnooze();
  }
</script>

{#if views.length > 0}
  <section class="upgrade" aria-labelledby={titleId}>
    <div class="upgrade-head">
      <h2 id={titleId}>Get more from EpiDev</h2>
      <Button variant="ghost" size="sm" onclick={snooze}>Not now</Button>
    </div>

    <div class="offer-grid">
      {#each views as view (view.offer.plan)}
        <PlanCard
          name={view.offer.name}
          price={view.price}
          interval={view.interval}
          features={view.offer.features.slice(0, 3)}
          featured={view.offer.featured}
          headingLevel={3}
        >
          <p class="offer-description">{view.offer.description}</p>
          {#if view.yearlyNote}<p class="offer-yearly">{view.yearlyNote}</p>{/if}
          {#snippet action()}
            <Button
              variant={view.offer.featured ? "primary" : "ghost"}
              loading={redirectingPlan === view.offer.plan}
              disabled={redirectingPlan !== null && redirectingPlan !== view.offer.plan}
              onclick={() => void startUpgrade(view.offer.plan)}
            >
              Upgrade to {view.offer.name}
            </Button>
          {/snippet}
        </PlanCard>
      {/each}
    </div>
  </section>
{/if}

<style>
  /* A band, not a panel: the plan cards are the surfaces here, and the brand does not
     nest one rounded card inside another. */
  .upgrade {
    display: flex;
    flex-direction: column;
    gap: 14px;
    margin-bottom: 18px;
  }
  .upgrade-head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 10px 12px;
  }
  .upgrade-head h2 {
    margin: 0;
    min-width: 0;
    color: var(--heading);
    font-size: 16px;
    font-weight: 600;
    letter-spacing: 0;
    overflow-wrap: break-word;
  }
  /* Two offers sit side by side where there is room and stack below it; the featured
     card's glow needs no extra room because it is a shadow, not a border. */
  .offer-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
    align-items: stretch;
    gap: 14px;
  }
  .offer-description {
    margin: 0;
    color: var(--text);
    overflow-wrap: break-word;
  }
  .offer-yearly {
    margin: 6px 0 0;
    color: var(--muted);
    overflow-wrap: break-word;
  }
  @media (max-width: 640px) {
    .offer-grid {
      grid-template-columns: 1fr;
    }
  }
</style>
